import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Button } from "./ui/button";
import { X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = new Image();
  image.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

export function PhotoCropModal({ imageSrc, aspectRatio = 1, shape = "round", onSave, onClose, saving }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
    onSave(blob);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="photo-crop-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900">Adjust Photo</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition">
            <X size={18} />
          </button>
        </div>

        <div className="relative w-full h-80 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            cropShape={shape}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-3 flex items-center gap-4 border-t bg-gray-50">
          <ZoomOut size={16} className="text-gray-400" />
          <input
            type="range"
            min={1} max={3} step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-blue-600"
            data-testid="crop-zoom-slider"
          />
          <ZoomIn size={16} className="text-gray-400" />
          <button
            onClick={() => setRotation((r) => (r + 90) % 360)}
            className="p-2 hover:bg-gray-200 rounded-lg transition"
            title="Rotate"
            data-testid="crop-rotate-btn"
          >
            <RotateCw size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t justify-end">
          <Button variant="outline" onClick={onClose} size="sm" data-testid="crop-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crop-save-btn">
            {saving ? "Saving..." : "Save Photo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
